import { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import Swal from 'sweetalert2';
import { logout } from '../../actions/authAction';
import removeLocalItem from '../../services/removeLocalStorage';
import http from '../../services/apicall';
import apis from '../../services/apis';

// Production values:
const IDLE_TIMEOUT = 13 * 60 * 1000; // 13 minutes in ms
const WARNING_DURATION = 2 * 60; // 2 minutes in seconds

// Testing values:
// const IDLE_TIMEOUT = 1 * 60 * 1000; // 1 minute in ms
// const WARNING_DURATION = 1 * 60; // 1 minute in seconds

function AutoLogout(props) {
  const history = useHistory();
  const [warningOpen, setWarningOpen] = useState(false);
  const idleTimer = useRef(null);
  const countdownInterval = useRef(null);
  const warningOpenRef = useRef(false); // Ref to track warning state without re-renders

  // ─── Logout handler ───
  const handleLogout = useCallback(async () => {
    // Clear any running timers
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);

    // Close SweetAlert if open
    if (Swal.isVisible()) {
      Swal.close();
    }

    // Call backend logout API to clean up server-side session
    try {
      const userCode = localStorage.getItem("user_code");
      if (userCode) {
        await http.post(apis.LOGOUT, { user_code: userCode, user_id: userCode, IM_USERID: userCode });
      }
    } catch (error) {
      console.error("Logout API failed:", error);
    }

    // Clear frontend state
    localStorage.removeItem("Token");
    localStorage.removeItem("user_code");
    localStorage.removeItem("bcl-depot-user");
    removeLocalItem();
    props.logout();

    setWarningOpen(false);
    warningOpenRef.current = false;

    history.push('/login');
  }, [history, props]);

  // ─── Warning popup with countdown ───
  const showWarningPopup = useCallback(() => {
    setWarningOpen(true);
    warningOpenRef.current = true;
    let timeLeft = WARNING_DURATION; // seconds

    const formatTime = (secs) => {
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    };

    Swal.fire({
      title: 'Session Expiring Soon',
      html: `<p style="font-size:16px;">You have been inactive.</p><p style="font-size:18px;">You will be logged out in <b id="swal-timer" style="color:#d33;">${formatTime(timeLeft)}</b></p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Continue Session',
      cancelButtonText: 'Logout Now',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      allowOutsideClick: false,
      allowEscapeKey: false,
      onOpen: () => {
        const timerEl = document.getElementById('swal-timer');
        countdownInterval.current = setInterval(() => {
          timeLeft -= 1;
          if (timerEl) {
            timerEl.textContent = formatTime(timeLeft);
          }
          if (timeLeft <= 0) {
            clearInterval(countdownInterval.current);
            handleLogout();
          }
        }, 1000);
      },
      onClose: () => {
        clearInterval(countdownInterval.current);
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // User clicked "Continue Session"
        setWarningOpen(false);
        warningOpenRef.current = false;
        resetTimer();
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        // User clicked "Logout Now"
        handleLogout();
      }
    });
  }, [handleLogout]);

  // ─── Reset the idle timer ───
  const resetTimer = useCallback(() => {
    // Don't reset if the warning popup is showing
    if (warningOpenRef.current) return;

    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      showWarningPopup();
    }, IDLE_TIMEOUT);
  }, [showWarningPopup]);

  // ─── Attach activity listeners ───
  useEffect(() => {
    if (!props.isLoggedIn) return;

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

    const handleActivity = () => {
      if (!warningOpenRef.current) {
        resetTimer();
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    resetTimer(); // Start the timer on mount

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
  }, [resetTimer, props.isLoggedIn]);

  return null; // This component renders nothing
}

const mapStateToProps = state => ({
  isLoggedIn: state.Auth.isLoggedIn
});
export default connect(mapStateToProps, { logout })(AutoLogout);
